import type { HypothesisNode, InvestigationRecord, TranscriptMessage, VerdictData } from "Apps/Health/Components/HypothesisReasoning/HypothesisReasoning.types";
import { types } from "mobx-state-tree";

export const OpsGenieHypothesisState = types
    .model({
        hypothesisConversationId: types.optional(types.string, ""),
        hypothesisIncidentId: types.optional(types.string, ""),
        hypothesisPhase: types.optional(types.string, "idle"),
        hypothesisTree: types.optional(types.frozen<HypothesisNode[]>(), []),
        hypothesisVerdict: types.optional(types.frozen<VerdictData | null>(), null),
        hypothesisTranscript: types.optional(types.frozen<TranscriptMessage[]>(), []),
        hypothesisHistory: types.optional(types.frozen<InvestigationRecord[]>(), []),
        hypothesisStartTime: types.optional(types.string, "")
    })
    .views((self) => ({
        get currentHypothesisConversationId() {
            return self.hypothesisConversationId;
        },
        get currentHypothesisIncidentId() {
            return self.hypothesisIncidentId;
        },
        get currentHypothesisPhase() {
            return self.hypothesisPhase;
        },
        get currentHypothesisTree() {
            return self.hypothesisTree;
        },
        get currentHypothesisVerdict() {
            return self.hypothesisVerdict;
        },
        get currentHypothesisTranscript() {
            return self.hypothesisTranscript;
        },
        get currentHypothesisHistory() {
            return self.hypothesisHistory;
        },
        get currentHypothesisStartTime() {
            return self.hypothesisStartTime;
        }
    }))
    .actions((self) => ({
        setHypothesisConversationId(id: string) {
            self.hypothesisConversationId = id;
        },
        setHypothesisIncidentId(id: string) {
            self.hypothesisIncidentId = id;
        },
        setHypothesisPhase(phase: string) {
            self.hypothesisPhase = phase;
        },
        setHypothesisTree(tree: HypothesisNode[]) {
            self.hypothesisTree = tree;
        },
        setHypothesisVerdict(verdict: VerdictData | null) {
            self.hypothesisVerdict = verdict;
        },
        setHypothesisTranscript(transcript: TranscriptMessage[]) {
            self.hypothesisTranscript = transcript;
        },
        setHypothesisStartTime(time: string) {
            self.hypothesisStartTime = time;
        },
        addHypothesisHistory(record: InvestigationRecord) {
            const MAX_PER_INCIDENT = 2;
            const MAX_INCIDENTS = 10;
            const history = self.hypothesisHistory;
            const sameIncident = history.filter((r) => r.incidentId === record.incidentId);
            const otherIncidents = history.filter((r) => r.incidentId !== record.incidentId);
            const uniqueIncidentIds = [...new Set(otherIncidents.map((r) => r.incidentId))];
            const trimmedOthers =
                uniqueIncidentIds.length >= MAX_INCIDENTS
                    ? otherIncidents.filter((r) => uniqueIncidentIds.slice(0, MAX_INCIDENTS - 1).includes(r.incidentId))
                    : otherIncidents;
            self.hypothesisHistory = [record, ...sameIncident.slice(0, MAX_PER_INCIDENT), ...trimmedOthers];
        },
        clearHypothesisState() {
            self.hypothesisConversationId = "";
            self.hypothesisIncidentId = "";
            self.hypothesisPhase = "idle";
            self.hypothesisTree = [];
            self.hypothesisVerdict = null;
            self.hypothesisTranscript = [];
        }
    }));
